import { Injectable, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {filter, first, map, switchMap, take, tap} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService, SessionService} from '@core';
import {ArraySort, BaseResult, HeaderSort, ModalService, TimeService, TransportType} from '@shared';

import {
    EmployeeUpdate,
    EmployeeUpdateResult,
    EmployeeView,
    EmployeeWorkShift,
    OptimizationService,
    ShiftSplitService,
    SkillRequirement,
    TaskIdInfo,
    Visit
} from 'planner-shared';

import {PatientService} from '../patient/patient.service';
import {ProblemInstanceService} from '../problem-instance/problem-instance.service';
import {
    EmployeesRestrictionsModalComponent
} from '../../employees-restrictions-modal/employees-restrictions-modal.component';
import {StopOptimizationHttpParams} from '../../interceptors/stop-optimization-http-params';
import {RosterDepartment} from '../../models/roster-department.model';
import {PatientTaskService} from '../patient-task/patient-task.service';
import {VisitMapperService} from '../task-id-info/visit-mapper.service';
import {RosterService} from "../roster/roster.service";

@Injectable({
    providedIn: 'root'
})
export class EmployeeService {
    private readonly httpClient = inject(HttpClient);
    private readonly sessionService = inject(SessionService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly patientTaskService = inject(PatientTaskService);
    private readonly patientService = inject(PatientService);
    private readonly timeService = inject(TimeService);
    private readonly modalService = inject(ModalService);
    private readonly visitMapper = inject(VisitMapperService);
    private readonly optimizationService = inject(OptimizationService);
    private readonly rosterService = inject(RosterService);
    private readonly shiftSplitService = inject(ShiftSplitService);
    private readonly featureFlagService = inject(FeatureFlagService);

    public employeeUpdateConfirm$ = new Subject();
    private employees$: ReplaySubject<EmployeeView[]>;

    constructor() {
        this.initEmployees();
    }

    public initEmployees(): void {
        this.employees$ = new ReplaySubject(1);
        this.getEmployeesFromInstance().subscribe(this.employees$);
    }

    public getEmployee(employeeWorkHourId: string): Observable<EmployeeView> {
        return this.employees$.asObservable().pipe(
            map(employees => {
                const employee = employees.find(i => i.workHourId === employeeWorkHourId);
                return employee;
            })
        );
    }

    public getSelectedEmployee(): Observable<EmployeeView> {
        return this.employees$.asObservable().pipe(
            first(),
            map(employees => employees.find(i => i.selected))
        );
    }

    public getEmployees(
        sortValue?: HeaderSort,
        searchPhrase?: string,
        onlyActives?: boolean,
        selectFirst?: boolean,
        visits?: Visit[]
    ): Observable<EmployeeView[]> {
        return this.employees$.asObservable().pipe(
            map(employees => {
                if (!employees || employees.length === 0) {
                    return [];
                }

                const existSelectedEmployee = employees.some(employee => employee.selected);

                if (onlyActives) {
                    employees = employees.filter(employee => employee.isActive);
                }

                if (searchPhrase) {
                    searchPhrase = searchPhrase.toLowerCase();
                    const employeeForPatient: EmployeeView[] = [];
                    if (visits) {
                        const patientsBySearch = this.patientService.getPatients()
                            .pipe(
                                map(patients => {
                                    return patients.filter(patient => patient.patientName.toLowerCase().includes(searchPhrase));
                                }));
                        patientsBySearch.subscribe(patients => {
                            patients.forEach(patient => {
                                const patientTasksObs = this.patientTaskService.getPatientTasks(patient.patientId);
                                patientTasksObs.subscribe(patientTasks => {
                                    patientTasks.forEach(patientTask => {
                                        employees.forEach(employee => {
                                            const employeePatientTasks: TaskIdInfo[] =
                                                this.visitMapper.mapToTaskIdInfo(visits, employee.workHourId);
                                            employeePatientTasks.forEach(employeeTask => {
                                                if (employeeTask.taskId === patientTask.taskId) {
                                                    employeeForPatient.push(employee);
                                                }
                                            });
                                        });
                                    });
                                });
                            });
                        });
                    }
                    employees = employees.filter(
                        employee =>
                            (employee.employeeId && employee.employeeId.toLowerCase().includes(searchPhrase)) ||
                            (employee.employeeName && employee.employeeName.toLowerCase().includes(searchPhrase)) ||
                            (employee.department && employee.department.toLowerCase().includes(searchPhrase)) ||
                            (employee.transport && employee.transport.toLowerCase().includes(searchPhrase)) ||
                            (employee.skillLevelDescription &&
                                employee.skillLevelDescription.toLowerCase().includes(searchPhrase))
                    );
                    employeeForPatient.forEach(patientEmployee => {
                        const hasDuplicate =
                            employees.some(employee => employee.employeeId === patientEmployee.employeeId);
                        if (!hasDuplicate) {
                            employees.push(patientEmployee);
                        }
                    });
                }

                this.featureFlagService.isEnabled(FeatureFlags.CriticalRosterPriority).subscribe(criticalRosterValue => {
                    this.problemInstanceService.rosterDepartmentIsPartOfInstance().subscribe(rosterValue => {
                        if (rosterValue && employees.some(employee => !employee.rosterDepartmentName) && criticalRosterValue) {
                            employees
                                .forEach(employee =>
                                {
                                    if (employee.hasError === undefined) {
                                        employee.hasError = employee.rosterDepartmentName ? false : true; }
                                });
                        }
                    });
                });

                if (sortValue) {
                    ArraySort.sort(
                        employees,
                        sortValue.sortBy,
                        sortValue.sortAscending,
                        sortValue.secondarySortBy,
                        sortValue.sortAscending
                    );
                }

                if (selectFirst && !existSelectedEmployee && employees.length > 0) {
                    employees[0].selected = true;
                }

                return employees;
            })
        );
    }

    public getActiveEmployeesCount(): Observable<number> {
        return this.problemInstanceService
            .getProblemInstance()
            .pipe(
                map(result => result.problemInstance.employeeWorkShifts.filter(employee => employee.isActive).length)
            );
    }

    public updateEditedEmployeesInfo(): Observable<BaseResult> {
        return this.getEmployees().pipe(
            map(employees => employees.filter(employee => employee.editing)),
            switchMap(employees =>
                this.problemInstanceService.getProblemInstance().pipe(
                    switchMap(result => {
                        const employeesToUpdate = employees.map(employee => {
                            const employeeWorkShift = result.problemInstance.employeeWorkShifts.find(
                                i => i.workHourId === employee.workHourId
                            );
                            if (employee.rosterDepartmentId) {
                                employeeWorkShift.rosterDepartmentId = employee.rosterDepartmentId;
                                employee.rosterDepartmentName = this.rosterService.getRosterNameById(employee.rosterDepartmentId, result.problemInstance);
                            } else if (employeeWorkShift.rosterDepartmentId) {
                                employee.rosterDepartmentId = employeeWorkShift.rosterDepartmentId;
                                employee.rosterDepartmentName = this.rosterService.getRosterNameById(employee.rosterDepartmentId, result.problemInstance);
                            }
                            return <EmployeeUpdate>{
                                employeeId: employeeWorkShift.employeeId,
                                workHourId: employeeWorkShift.workHourId,
                                skillLevelId: employee.skillLevelId,
                                transport: employee.transport,
                                isActive: employee.isActive,
                                fromDate: employeeWorkShift.fromDate,
                                fromTime: this.timeService.convertTime(employee.fromTime),
                                toDate: employeeWorkShift.toDate,
                                toTime: this.timeService.convertTime(employee.toTime),
                                rideSharing: employee.transport
                                    === TransportType.Bicycle ? false : employee.rideSharing,
                                rideSharingRole: employee.rideSharingRole,
                                rosterDepartmentId: employee.rosterDepartmentId
                            };
                        });
                        return this.updateEmployees(employeesToUpdate).pipe(
                            tap(updateResult => {
                                result.problemInstance.taskWorkShiftCompatibility =
                                    updateResult.taskWorkShiftCompatibility;
                                result.problemInstance.officeInfo.numberOfTasksWithoutMainResponsibles =
                                    updateResult.numberOfTasksWithoutMainResponsibles;
                                this.mapEditedEmployeesInfoToEmployeesWorkShift(
                                    employees,
                                    result.problemInstance.employeeWorkShifts
                                );
                            })
                        );
                    })
                )
            ),
            tap(() => {
                this.employeeUpdateConfirm$.next(null);
                this.employeeUpdateConfirm$ = new Subject();
            })
        );
    }

    public openEmployeesRestrictionModal(taskId: string): void {
        this.modalService.openCustomModal(EmployeesRestrictionsModalComponent, () => {
        }, null, {
            taskId
        });
    }

    public updateEmployeeLocalParams(employeeToUpdate: EmployeeView): void {
        this.employees$
            .pipe(
                take(1),
                tap(employees => {
                    employees
                        .find(employee => employee.employeeId === employeeToUpdate.employeeId)
                        .hasUnpairedSyncTask = employeeToUpdate.hasUnpairedSyncTask;
                    this.employees$.next(employees);
                    this.employeeUpdateConfirm$.next(null);
                })
            ).subscribe();
    }

    private mapEditedEmployeesInfoToEmployeesWorkShift(
        employees: EmployeeView[],
        employeeWorkShifts: EmployeeWorkShift[]
    ): void {
        employees.forEach(employee => {
            const employeeWorkShift = employeeWorkShifts.find(i => i.workHourId === employee.workHourId);
            employeeWorkShift.skillLevelId = employee.skillLevelId;
            employeeWorkShift.transport = employee.transport;
            employeeWorkShift.isActive = employee.isActive;
            employeeWorkShift.fromTime = this.timeService.convertTime(employee.fromTime);
            employeeWorkShift.toTime = this.timeService.convertTime(employee.toTime);
            employeeWorkShift.rideSharing = employee.transport
                === TransportType.Bicycle ? false : employee.rideSharing;
            employeeWorkShift.rideSharingRole = employee.rideSharingRole;
            employeeWorkShift.rosterDepartmentId = employee.rosterDepartmentId;
            this.shiftSplitService.checkSplitWindowUpdate(employeeWorkShift);
        });
    }

    private getEmployeesFromInstance(): Observable<EmployeeView[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            filter(result => result && result.problemInstance != null),
            map(result =>
                result.problemInstance.employeeWorkShifts.map(employee => {
                    return this.mapEmployeeFromInstance(
                        employee,
                        result.problemInstance.skillLevels,
                        result.problemInstance.officeInfo.defaultTransportMode,
                        result.problemInstance.rosterDepartments
                    );
                })
            )
        );
    }

    private mapEmployeeFromInstance(
        employee: EmployeeWorkShift,
        skillLevels: SkillRequirement[],
        defaultTransportMode: TransportType,
        rosterDepartments: RosterDepartment[]
    ): EmployeeView {
        const skillLevel = skillLevels.find(item => item.skillLevelId === employee.skillLevelId);
        return <EmployeeView>{
            employeeId: employee.employeeId,
            workHourId: employee.workHourId,
            employeeName: employee.employeeName,
            isActive: employee.isActive,
            department: employee.department,
            shiftCode: employee.shiftCode,
            transport: employee.transport ? employee.transport : defaultTransportMode,
            skillLevelId: employee.skillLevelId,
            skillLevelDescription: skillLevel ? skillLevel.skillLevelDescription : null,
            fromTime: employee.toTime ? this.timeService.convertTime(employee.fromTime, true) : null,
            fromDate: employee.fromDate,
            toTime: employee.toTime ? this.timeService.convertTime(employee.toTime, true) : null,
            toDate: employee.toDate,
            allocatedTasks: [],
            shiftType: employee.shiftType,
            showRoster: rosterDepartments != null && employee.rosterDepartmentId != null,
            rosterDepartmentName: rosterDepartments != null && employee.rosterDepartmentId != null ?
                this.rosterService.getRosterNameById(employee.rosterDepartmentId) : null,
            rideSharing: employee.transport
                === TransportType.Bicycle ? false : employee.rideSharing,
            rideSharingRole: employee.rideSharingRole
        };
    }

    private updateEmployees(employeesToUpdate: EmployeeUpdate[]): Observable<EmployeeUpdateResult> {
        const url = `${this.sessionService.getApiUrl()}/update_employees/${this.sessionService.requestId}`;
        const body = {
            employeeUpdates: JSON.parse(JSON.stringify(employeesToUpdate))
        };
        this.optimizationService.stopOptimizationProcess().subscribe();
        return this.httpClient.put<EmployeeUpdateResult>(url, body, {params: new StopOptimizationHttpParams(false).getHttpParams()});
    }
}
